import { useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import { ChevronLeftIcon, ChevronRightIcon } from "@/lib/icons";

/**
 * A small, dependency-free inline month calendar.
 *
 * Always visible (no popover) — the user picks a day with a single click.
 * Weeks are Monday-first and month/year is labelled in Finnish via `Intl`,
 * matching the codebase's hand-rolled date approach (see `lib/weekUtils.ts`)
 * rather than pulling in a date library.
 *
 * The value is the same `"YYYY-MM-DD"` string used by `<input type="date">`,
 * formatted from local-time Y/M/D so there is no UTC off-by-one.
 */

const WEEKDAYS_FI = ["ma", "ti", "ke", "to", "pe", "la", "su"];

/** Rows the day grid always renders, so the calendar's height never changes. */
const WEEKS_SHOWN = 6;

/** Zero-padded local-time `"YYYY-MM-DD"` for the given Y/M/D. */
function toYmd(year: number, month: number, day: number): string {
  const mm = String(month + 1).padStart(2, "0");
  const dd = String(day).padStart(2, "0");
  return `${year}-${mm}-${dd}`;
}

/** Parse `"YYYY-MM-DD"` into Y/M/D, or null if absent/malformed. */
function parseYmd(value: string): { year: number; month: number; day: number } | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  return { year: Number(match[1]), month: Number(match[2]) - 1, day: Number(match[3]) };
}

export function InlineCalendar({
  value,
  onChange,
  className,
}: {
  value: string;
  onChange: (yyyyMmDd: string) => void;
  className?: string;
}) {
  const selected = parseYmd(value);

  const now = new Date();
  const today = { year: now.getFullYear(), month: now.getMonth(), day: now.getDate() };

  // The month currently on screen. Defaults to the selected month, else today.
  const [view, setView] = useState<{ year: number; month: number }>(
    selected
      ? { year: selected.year, month: selected.month }
      : { year: today.year, month: today.month },
  );

  // Follow the value onto its own month whenever it changes from the outside —
  // a preset chip near month end lands in the next month, and without this the
  // grid stayed put and the selected day was simply not on screen. Tracked
  // against the last value rather than against `view` so the arrows still win:
  // navigating away does not snap back until the value itself changes.
  const [syncedValue, setSyncedValue] = useState(value);
  if (value !== syncedValue) {
    setSyncedValue(value);
    if (selected && (selected.year !== view.year || selected.month !== view.month)) {
      setView({ year: selected.year, month: selected.month });
    }
  }

  const monthLabel = useMemo(
    () =>
      new Intl.DateTimeFormat("fi-FI", { month: "long", year: "numeric" }).format(
        new Date(view.year, view.month, 1),
      ),
    [view],
  );

  // Build the grid: leading blanks for days before the 1st (Monday-first),
  // then one cell per day of the month, then trailing blanks out to a fixed
  // six rows.
  //
  // The padding is what keeps the calendar a constant height. A month spans
  // four to six rows depending on where the 1st falls, so without it the grid
  // grew or shrank by a row on some month changes — and because the dialog is
  // vertically centred, that moved the navigation arrows out from under the
  // cursor mid-click. Six rows covers the worst case (31 days after six
  // leading blanks = 37 cells).
  const cells = useMemo(() => {
    const firstDay = new Date(view.year, view.month, 1).getDay(); // 0=Sun … 6=Sat
    const leading = (firstDay + 6) % 7; // shift so Monday=0
    const daysInMonth = new Date(view.year, view.month + 1, 0).getDate();
    const result: (number | null)[] = [];
    for (let i = 0; i < leading; i++) result.push(null);
    for (let d = 1; d <= daysInMonth; d++) result.push(d);
    while (result.length < WEEKS_SHOWN * 7) result.push(null);
    return result;
  }, [view]);

  const goToMonth = (delta: number) => {
    setView((prev) => {
      const next = new Date(prev.year, prev.month + delta, 1);
      return { year: next.getFullYear(), month: next.getMonth() };
    });
  };

  return (
    <div className={cn("w-full max-w-[16rem] rounded-md border bg-background p-2", className)}>
      {/* Header: month navigation */}
      <div className="mb-1 flex items-center justify-between">
        <button
          type="button"
          onClick={() => goToMonth(-1)}
          aria-label="Edellinen kuukausi"
          className="inline-flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-accent-foreground"
        >
          <ChevronLeftIcon className="h-3.5 w-3.5" />
        </button>
        <span className="text-xs font-medium capitalize">{monthLabel}</span>
        <button
          type="button"
          onClick={() => goToMonth(1)}
          aria-label="Seuraava kuukausi"
          className="inline-flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-accent-foreground"
        >
          <ChevronRightIcon className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Weekday headings */}
      <div className="grid grid-cols-7 gap-0.5">
        {WEEKDAYS_FI.map((wd) => (
          <div
            key={wd}
            className="flex h-6 items-center justify-center text-[11px] font-medium text-muted-foreground"
          >
            {wd}
          </div>
        ))}
      </div>

      {/* Day grid */}
      <div className="grid grid-cols-7 gap-0.5">
        {cells.map((day, idx) => {
          if (day === null) return <div key={`blank-${idx}`} className="h-7" />;
          const isSelected =
            !!selected &&
            selected.year === view.year &&
            selected.month === view.month &&
            selected.day === day;
          const isToday =
            today.year === view.year && today.month === view.month && today.day === day;
          return (
            <button
              key={day}
              type="button"
              onClick={() => onChange(toYmd(view.year, view.month, day))}
              className={cn(
                "flex h-7 items-center justify-center rounded-md text-xs transition-colors",
                isSelected
                  ? "bg-primary text-primary-foreground hover:bg-primary/90"
                  : "hover:bg-accent hover:text-accent-foreground",
                !isSelected && isToday && "border border-primary/50 font-medium",
              )}
            >
              {day}
            </button>
          );
        })}
      </div>
    </div>
  );
}
